import React, { memo, useMemo } from "react";
import { Box, Typography, LinearProgress, Chip, Fade } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import FlexBetween from "components/FlexBetween";


/* ================= DEEP COMPARE ================= */
const isEqual = (a, b) => {
    if (a === b) return true;

    if (
        typeof a !== "object" ||
        typeof b !== "object" ||
        a === null ||
        b === null
    )
        return false;

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (let key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!isEqual(a[key], b[key])) return false;
    }

    return true;
};

/* ================= TYPE DETECT ================= */
const detectMode = (props) => {
    if (typeof props === "function") return "custom";

    if (!props || typeof props !== "object") return "empty";

    if ("progress" in props || "divisor" in props) return "progress";

    return "list";
};

/* ================= LIST MODE ================= */
const ListContent = memo(({ data, icon }) => (
    <FlexBetween>
        <FlexBetween>{icon}</FlexBetween>
        <Box
            flexGrow={1}
            ml={1}
            display="flex"
            flexDirection="column"
            gap={1}
        >
            {Object.entries(data).map(([key, value]) => (
                <FlexBetween key={key}>
                    <Typography variant="h6">{key}</Typography>
                    <Typography variant="h6">{value}</Typography>
                </FlexBetween>
            ))}
        </Box>
    </FlexBetween>
));

/* ================= PROGRESS MODE ================= */
const ProgressContent = memo(({ data, icon }) => (
    <Box
        display={"flex"}
        flexDirection={"column"}
        gap={1}
    >
        <FlexBetween>
            <Typography variant="h6">{data.title}</Typography>
            <FlexBetween>{icon}</FlexBetween>
        </FlexBetween>

        {data.subtitle && (
            <Typography variant="body2" color="text.secondary">
                {data.subtitle}
            </Typography>
        )}

        <FlexBetween>
            <Typography variant="body2">
                {data.divisor}
                {data.dividend ? ` / ${data.dividend}` : ""}
            </Typography>

            <Typography variant="body2">{data.rate}</Typography>
        </FlexBetween>

        <LinearProgress
            variant="determinate"
            value={data.progress || 0}
            sx={{ height: 5, borderRadius: 5 }}
        />

        {data.status && (
            <Box>
                <Chip
                    variant="outlined"
                    size="small"
                    label={data.status}
                    color={data.status === "Complete"
                        ? "success"
                        : data.status === "In Progress"
                            ? "warning"
                            : "default"
                    }
                />
            </Box>
        )}
    </Box>
));

/* ================= MAIN ================= */
const ProgressBox = ({ icon, props }) => {
    const mode = useMemo(() => detectMode(props), [props]);

    const content = useMemo(() => {
        switch (mode) {
        case "custom":
            return props();

        case "list":
            return <ListContent data={props} icon={icon} />;

        case "progress":
            return <ProgressContent data={props} icon={icon} />;

        default:
            return null;
        }
    }, [mode, props, icon]);


    return (
        <Box
            sx={{
                display: 'flex',         
                flexDirection: 'column',
                justifyContent: 'space-around',
                borderRadius: 1,
                border: "1px solid",
                borderColor: "neutral.deep",
                backgroundColor: "slate.lightest",
                px: 1.5,
                overflow: "hidden",
            }}
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={mode}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                >
                    <Fade in timeout={200}>
                        <Box>{content}</Box>
                    </Fade>
                </motion.div>
            </AnimatePresence>
        </Box>
    );
};


export default memo(ProgressBox, (prev, next) => {
    return isEqual(prev.props, next.props) && prev.icon === next.icon;
});